import React, { useState, useCallback, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import Cropper from 'react-easy-crop'
import { X, Check, ZoomIn, RotateCw, Move, Sliders, Crop as CropIcon, Trash2, Camera, RefreshCw } from 'lucide-react'
import { useAuth } from '@/contexts/auth-context'

interface Point {
  x: number
  y: number
}

interface Area {
  width: number
  height: number
  x: number
  y: number
}

interface ImageCropModalProps {
  isOpen: boolean
  imageSrc: string
  onClose: () => void
  onSave: (croppedImageBlob: Blob) => void
  aspect?: number
  // Contexto para simulação real de projeto
  projectTitle?: string
  projectDescription?: string
  projectCategory?: string
  itinerario?: string
  participouEdital?: string
  ganhouPremio?: string
}

const ImageCropModal: React.FC<ImageCropModalProps> = ({
  isOpen,
  imageSrc,
  onClose,
  onSave,
  aspect = 15 / 4,
  projectTitle,
  projectDescription,
  projectCategory,
  itinerario,
  participouEdital,
  ganhouPremio
}) => {
  const { user } = useAuth()
  const authorName = user?.nome || 'Estudante'
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [rotation, setRotation] = useState(0)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [activePreview, setActivePreview] = useState<'card' | 'banner'>('banner')
  
  useEffect(() => {
    if (!croppedAreaPixels || !imageSrc) return;
    
    const timer = setTimeout(async () => {
      try {
        const blob = await getCroppedImg(imageSrc, croppedAreaPixels, rotation);
        const url = URL.createObjectURL(blob);
        setPreviewUrl(prev => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });
      } catch (e) {
        console.error('Error generating preview:', e);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [croppedAreaPixels, rotation, zoom, crop]);

  // Clean up URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    }
  }, [previewUrl]);

  const onCropComplete = useCallback((croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels)
  }, [])

  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image()
      image.addEventListener('load', () => resolve(image))
      image.addEventListener('error', (error) => reject(error))
      image.setAttribute('crossOrigin', 'anonymous')
      image.src = url
    })

  const getCroppedImg = async (
    imageSrc: string,
    pixelCrop: Area,
    rotation = 0
  ): Promise<Blob> => {
    const image = await createImage(imageSrc)
    
    // 1. Criar um canvas temporário para desenhar a imagem rotacionada
    const tempCanvas = document.createElement('canvas')
    const tempCtx = tempCanvas.getContext('2d')
    if (!tempCtx) {
      throw new Error('No 2d context')
    }

    const maxSize = Math.max(image.width, image.height)
    const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2))

    tempCanvas.width = safeArea
    tempCanvas.height = safeArea

    tempCtx.translate(safeArea / 2, safeArea / 2)
    tempCtx.rotate((rotation * Math.PI) / 180)
    tempCtx.translate(-safeArea / 2, -safeArea / 2)

    tempCtx.drawImage(
      image,
      safeArea / 2 - image.width * 0.5,
      safeArea / 2 - image.height * 0.5
    )

    // 2. Criar o canvas final de recorte
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      throw new Error('No 2d context')
    }

    canvas.width = pixelCrop.width
    canvas.height = pixelCrop.height

    // Preenche o fundo com BRANCO quando a foto for de outro formato
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    // Desenha a porção recortada do canvas temporário preservando transparência sobre o fundo branco
    ctx.drawImage(
      tempCanvas,
      safeArea / 2 - image.width * 0.5 + pixelCrop.x,
      safeArea / 2 - image.height * 0.5 + pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    )

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob)
        } else {
          reject(new Error('Canvas is empty'))
        }
      }, 'image/jpeg', 0.95)
    })
  }

  const handleSave = async () => {
    if (!croppedAreaPixels) return

    setIsSaving(true)
    try {
      const croppedImageBlob = await getCroppedImg(imageSrc, croppedAreaPixels, rotation)
      onSave(croppedImageBlob)
    } catch (error) {
      console.error('Erro ao recortar imagem:', error)
      alert('Erro ao processar a imagem. Tente novamente.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleReset = () => {
    setZoom(1)
    setRotation(0)
    setCrop({ x: 0, y: 0 })
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-[95vw] max-w-[1440px] h-[80vh] mb-12 flex flex-col md:flex-row overflow-hidden"
          >
            {/* Left Side - Image Area */}
            <div className="flex-1 relative bg-gray-100 dark:bg-gray-900 overflow-hidden">
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={aspect}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onRotationChange={setRotation}
                onCropComplete={onCropComplete}
                showGrid={true}
                objectFit="contain"
                restrictPosition={false}
                style={{
                  containerStyle: {
                    backgroundColor: '#ffffff'
                  }
                }}
              />

              {/* Mobile View Toggle (could be improved, but keeps layout simple) */}
              <div className="absolute top-4 left-4 md:hidden z-10">
                <span className="px-3 py-1 bg-black/50 text-white text-xs rounded-full backdrop-blur-sm">
                  {Math.round(zoom * 100)}%
                </span>
              </div>
              {/* Floating Controls (Glassmorphism) */}
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 w-[90%] max-w-md bg-white/80 dark:bg-gray-900/80 backdrop-blur-md p-4 rounded-2xl shadow-lg border border-white/20 dark:border-gray-700/30 flex flex-col gap-3 z-10">
                <div className="flex items-center gap-3">
                  <ZoomIn className="w-4 h-4 text-gray-600 dark:text-gray-300 shrink-0" />
                  <button onClick={() => setZoom(Math.max(0.5, zoom - 0.1))} className="w-6 h-6 flex items-center justify-center rounded-full bg-gray-200/50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">-</button>
                  <input
                    type="range"
                    min={0.5}
                    max={3}
                    step={0.05}
                    value={zoom}
                    onChange={(e) => setZoom(Number(e.target.value))}
                    className="flex-1 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer accent-blue-600"
                  />
                  <button onClick={() => setZoom(Math.min(3, zoom + 0.1))} className="w-6 h-6 flex items-center justify-center rounded-full bg-gray-200/50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">+</button>
                  <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 w-9 text-right">{Math.round(zoom * 100)}%</span>
                </div>
                <div className="flex items-center gap-3">
                  <RotateCw className="w-4 h-4 text-gray-600 dark:text-gray-300 shrink-0" />
                  <button onClick={() => setRotation((rotation - 5 + 360) % 360)} className="w-6 h-6 flex items-center justify-center rounded-full bg-gray-200/50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">-</button>
                  <input
                    type="range"
                    min={0}
                    max={360}
                    step={1}
                    value={rotation}
                    onChange={(e) => setRotation(Number(e.target.value))}
                    className="flex-1 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer accent-blue-600"
                  />
                  <button onClick={() => setRotation((rotation + 5) % 360)} className="w-6 h-6 flex items-center justify-center rounded-full bg-gray-200/50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">+</button>
                  <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 w-9 text-right">{rotation}°</span>
                </div>
              </div>

            </div>

            {/* Right Side - Controls Sidebar */}
            <div className="w-full md:w-[500px] shrink-0 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 flex flex-col">

              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Editar imagem</h2>
                <button
                  onClick={onClose}
                  className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors text-gray-500"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 flex flex-col p-6 overflow-y-auto">
                

                {/* Seção de Previsão */}
                <div className="flex-1 flex flex-col">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                      <Camera className="w-4 h-4" /> Simulação Visual
                    </h3>
                  </div>

                  {/* Toggle (Segmented Control) */}
                  <div className="flex p-1 bg-gray-100 dark:bg-gray-800 rounded-lg mb-5">
                    <button
                      onClick={() => setActivePreview('banner')}
                      className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${
                        activePreview === 'banner' 
                          ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' 
                          : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                      }`}
                    >
                      Página Completa
                    </button>
                    <button
                      onClick={() => setActivePreview('card')}
                      className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${
                        activePreview === 'card' 
                          ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' 
                          : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                      }`}
                    >
                      Card (Dashboard)
                    </button>
                  </div>

                  {/* Renderização do Mockup Escolhido */}
                  <div className="flex items-center justify-center flex-1">
                    {activePreview === 'card' ? (
                      <div className="w-full max-w-[340px] bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden flex flex-col hover:shadow-xl transition-all duration-300 transform scale-[0.85] origin-center">
                        <div className="relative w-full h-[192px] bg-gray-100 dark:bg-gray-900 shrink-0">
                          {previewUrl ? (
                            <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-xs text-gray-400">Processando...</div>
                          )}
                          <div className="absolute top-4 left-4 z-10 flex flex-col gap-2">
                            <span className="px-3 py-1 bg-amber-500 text-white text-xs font-semibold rounded-full shadow-lg uppercase flex items-center gap-1.5 backdrop-blur-sm">
                              💡 IDEAÇÃO
                            </span>
                          </div>
                        </div>
                        <div className="p-6 flex-1 flex flex-col text-left">
                          <h3 className="text-lg font-bold text-[#00aceb] mb-3 line-clamp-2 leading-tight">
                            {projectTitle || "Título do Projeto"}
                          </h3>
                          <p className="text-gray-500 dark:text-gray-400 text-sm mb-4 line-clamp-3 leading-relaxed font-medium">
                            {projectDescription || "Uma breve descrição do seu projeto aparecerá aqui para que os visitantes conheçam a sua ideia."}
                          </p>
                          <div className="flex flex-wrap gap-2 mb-4">
                            {(!itinerario && !participouEdital && !ganhouPremio) ? (
                              <>
                                <span className="px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 text-[10px] font-bold rounded-md uppercase tracking-wide border border-blue-100 dark:border-blue-800">
                                  ITINERÁRIO
                                </span>
                                <span className="px-2 py-1 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 text-[10px] font-bold rounded-md uppercase tracking-wide border border-amber-100 dark:border-amber-800">
                                  EDITAL
                                </span>
                                <span className="px-2 py-1 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300 text-[10px] font-bold rounded-md uppercase tracking-wide border border-yellow-100 dark:border-yellow-800">
                                  PRÊMIO
                                </span>
                              </>
                            ) : (
                              <>
                                {itinerario === 'Sim' && (
                                  <span className="px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 text-[10px] font-bold rounded-md uppercase tracking-wide border border-blue-100 dark:border-blue-800">
                                    ITINERÁRIO
                                  </span>
                                )}
                                {participouEdital === 'Sim' && (
                                  <span className="px-2 py-1 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 text-[10px] font-bold rounded-md uppercase tracking-wide border border-amber-100 dark:border-amber-800">
                                    EDITAL
                                  </span>
                                )}
                                {ganhouPremio === 'Sim' && (
                                  <span className="px-2 py-1 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300 text-[10px] font-bold rounded-md uppercase tracking-wide border border-yellow-100 dark:border-yellow-800">
                                    PRÊMIO
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                          <div className="mt-auto pt-4 border-t border-gray-100 dark:border-gray-700">
                            <div className="flex items-end justify-between">
                              <div className="space-y-0.5">
                                <div className="flex items-center gap-1.5 text-xs text-gray-400">
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><rect width="18" height="18" x="3" y="4" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                                  <span>Criado: <span className="font-semibold text-gray-600 dark:text-gray-300">{new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })}</span></span>
                                </div>
                                <div className="flex items-center gap-1.5 text-xs text-gray-400">
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                                  <span>Atualizado: <span className="font-semibold text-gray-600 dark:text-gray-300">{new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })}</span></span>
                                </div>
                              </div>
                              <span className="text-[#00aceb] font-bold flex items-center gap-1 text-sm">
                                Ver Detalhes 
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="w-full h-full flex flex-col justify-center transform scale-95 origin-center">
                        <p className="text-xs text-gray-500 mb-4 text-center">Formato panorâmico (Página do Projeto)</p>
                        <div className="relative w-full rounded-2xl overflow-hidden shadow-2xl bg-gray-900 border border-gray-200 dark:border-gray-700/50" style={{ aspectRatio: aspect }}>
                          {previewUrl ? (
                            <img src={previewUrl} alt="Preview da Página" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-sm text-gray-400">Processando...</div>
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/60 to-transparent flex flex-col justify-end p-6" />
                          <div className="absolute inset-x-0 mx-6 bottom-6 flex flex-col justify-end">
                            <h1 className="text-2xl md:text-3xl font-black text-white tracking-tight leading-tight mb-2 truncate drop-shadow-md">
                              {projectTitle || "Título do Projeto"}
                            </h1>
                            <div className="flex items-center gap-3 text-gray-300 pt-1">
                               <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-lg border-2 border-white/20">
                                 {authorName ? authorName.charAt(0).toUpperCase() : 'E'}
                               </div>
                               <div className="flex flex-col">
                                 <span className="text-xs font-medium opacity-80 leading-none">Publicado por</span>
                                 <span className="font-bold text-white text-sm leading-tight mt-1">{authorName}</span>
                               </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer Buttons */}
              <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3 bg-gray-50 dark:bg-gray-800/50">
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-full text-sm font-semibold shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSaving ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    'Salvar alterações'
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default ImageCropModal
